import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Star, Save, Share2, RotateCcw, Play, AlertCircle, Check, FileDown, Mail, Zap, ArrowRight, GitBranch } from "lucide-react";
import { toast } from "sonner";
import { SafetyBanner } from "./SafetyBanner";
import { isFavorite, toggleFavorite, saveCalculation, pushRecent, getSettings, getSubseaProfile, saveSubseaProfile } from "@/lib/storage";
import { Download as DownloadIcon, Upload as UploadIcon } from "lucide-react";
import { getDisplayUnit, toImperialValue, fromImperialValue, getOppositeReading, convertLabeledMap } from "@/lib/metricUnits";
import { exportCalculationToPDF } from "@/lib/pdfExport";
import { getRelatedTo, getRelatedFrom, CALCULATORS } from "@/lib/catalog";
import { useEntitlement } from "@/lib/entitlement";
import { useCalcPrefill } from "@/lib/rce/useCalcPrefill";
import { isProActive, isCalculatorAccessible, FeatureLockedError } from "@/lib/featureAccess";
import UpgradePromptModal from "./UpgradePromptModal";
import SubscribeModal from "./SubscribeModal";
import LibraryPicker from "./LibraryPicker";
import { fieldValuesFromLibraryItem, provenanceLineFromRef, provenanceLineLongFromRef, findLibraryItem } from "@/lib/reference";
import SafetyConfirmationGate from "@/components/SafetyConfirmationGate";
import SectionTable from "@/components/SectionTable";
import { scopedSession } from "@/lib/accountScope";
import { trackFeatureOpened, trackCalculatorAttempted, trackPremiumBlocked, trackUpgradeModalViewed } from "@/lib/telemetry";

const HANDOFF_KEY = "rsc.prefill.v1";

// Phase A inline upgrade toast for action-level blocks. Shows a clean
// "Upgrade to unlock" prompt with a one-click link to /subscription instead
// of crashing on FeatureLockedError.
function _toastUpgrade(navigate, message) {
  toast.error(message || "This action requires RigSentinel Pro.", {
    duration: 5000,
    action: { label: "Upgrade", onClick: () => navigate("/pricing") },
  });
}

// F1a UX follow-up: generic mode-based field show/hide.
// A catalog field may declare `visibleWhen: { field, startsWith }` to be
// conditionally rendered when another field's current value starts with one
// of the given prefixes (case-sensitive, matches the select option labels
// emitted by the catalog dropdown, e.g. "Mode A — …" / "Mode B — …").
// Fields without `visibleWhen` are always shown. The hidden field's value
// is preserved in state but not collected from the user; the calc function
// for the active mode does not read the opposite-mode field, so this is
// purely a UX concern — no formula, validation, or disclaimer change.
function isFieldVisible(field, values) {
  const vw = field && field.visibleWhen;
  if (!vw || !vw.field) return true;
  const current = String(values[vw.field] ?? "");
  const prefixes = Array.isArray(vw.startsWith)
    ? vw.startsWith
    : (vw.startsWith ? [vw.startsWith] : []);
  if (prefixes.length === 0) return true;
  return prefixes.some((p) => current.startsWith(p));
}

export default function CalculatorRunner({ calc }) {
  const ent = useEntitlement();
  const isPro = isProActive(ent);

  // RC-1 Reusable RCE Consumer Framework — calculator prefill.
  // `prefill` is an object keyed by field.key with values resolved from
  // the active Engineering Session. Empty when no session is active or
  // the catalog row declares no `rcePath` bindings. We feed it into the
  // init seed below; library picks, cross-calc handoff, and any user
  // edits all happen later and naturally override the prefill — matching
  // the precedence documented in the feasibility report.
  const { prefill: rcePrefill } = useCalcPrefill(calc);

  const init = useMemo(() => {
    const s = {};
    for (const f of calc.fields) {
      if (rcePrefill && Object.prototype.hasOwnProperty.call(rcePrefill, f.key)) {
        s[f.key] = rcePrefill[f.key];
        continue;
      }
      if (f.type === "select" && f.options?.length) s[f.key] = f.options[0];
      else s[f.key] = "";
    }
    return s;
  }, [calc, rcePrefill]);

  const [values, setValues] = useState(init);
  const navigate = useNavigate();
  const relatedTo = useMemo(() => getRelatedTo(calc.id), [calc.id]);
  const relatedFrom = useMemo(() => getRelatedFrom(calc.id), [calc.id]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [fav, setFav] = useState(isFavorite(calc.id));
  const [system, setSystem] = useState(getSettings().units || "imperial");
  const [wellName, setWellName] = useState("");
  const [rigName, setRigName] = useState("");
  const [note, setNote] = useState("");
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  // Owner Dashboard V1 — non-sensitive telemetry: this calculator was opened.
  useEffect(() => {
    trackFeatureOpened(calc.id, "calculator");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calc.id]);

  // Apply default well/rig from settings + consume any prefill handoff
  useEffect(() => {
    const s = getSettings();
    setWellName((prev) => prev || s.defaultWell || "");
    setRigName((prev) => prev || s.defaultRig || "");
    try {
      const raw = scopedSession.getItem(HANDOFF_KEY);
      if (raw) {
        const ho = JSON.parse(raw);
        if (ho && ho.targetCalcId === calc.id && ho.fieldKey && ho.value != null) {
          setValues((prev) => ({ ...prev, [ho.fieldKey]: String(ho.value) }));
          toast.message(`Prefilled ${ho.fieldKey}`, { description: `From ${ho.sourceName || "previous calculation"}` });
        }
        scopedSession.removeItem(HANDOFF_KEY);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calc.id]);

  const handleSendTo = (link) => {
    if (!result) { toast.error("Run the calculation first"); return; }
    scopedSession.setItem(HANDOFF_KEY, JSON.stringify({
      targetCalcId: link.id,
      fieldKey: link.fieldKey,
      value: result.result, // pass the imperial result (deterministic core value)
      sourceName: calc.name,
    }));
    navigate(`/calc/${link.id}`);
  };

  const setField = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  // R1a Reference Data Library — track which fields were pre-filled from
  // the library so we can (a) render the "From library / Overridden" badge,
  // and (b) append a provenance line to result.steps when Calculate runs.
  // libraryRefs is a map keyed by calculator-field key.
  const [libraryRefs, setLibraryRefs] = useState({});
  const [pickerOpenFor, setPickerOpenFor] = useState(null); // { fieldKey, libraryKind, anchorLabel } | null

  const setFieldFromLibrary = (fieldKey, value, ref) => {
    setValues((prev) => ({ ...prev, [fieldKey]: value }));
    setLibraryRefs((prev) => ({ ...prev, [fieldKey]: { ...ref, originalValue: value } }));
  };

  const setFieldUserEdited = (fieldKey, v) => {
    setValues((prev) => ({ ...prev, [fieldKey]: v }));
    setLibraryRefs((prev) => {
      const existing = prev[fieldKey];
      if (!existing) return prev;
      // Field was previously filled from library; user just edited it →
      // mark as overridden but keep the source-of-truth so the badge can
      // still show "Overridden — was <library value>".
      if (String(v) === String(existing.originalValue) && !existing.overridden) {
        return prev;
      }
      return { ...prev, [fieldKey]: { ...existing, overridden: true } };
    });
  };

  const openLibraryPicker = (field) => {
    if (!field.libraryKind) return;
    // R2b: libraryKind may be a single string (legacy) or an array of kinds.
    const kinds = Array.isArray(field.libraryKind) ? field.libraryKind : [field.libraryKind];
    setPickerOpenFor({ fieldKey: field.key, libraryKind: kinds, libraryKindSingle: kinds[0], anchorLabel: field.label });
  };

  const handleLibraryPick = (item, libraryKind) => {
    if (!pickerOpenFor) return;
    // R2c: safety-sensitive kinds route through SafetyConfirmationGate before
    // the value is committed to the field.
    if (libraryKind === "formation_template_section") {
      setPickerOpenFor(null);
      setPendingGate({ item, libraryKind, fieldKey: pickerOpenFor.fieldKey });
      return;
    }
    const mapped = fieldValuesFromLibraryItem(libraryKind, item, pickerOpenFor.fieldKey);
    if (mapped) {
      setFieldFromLibrary(pickerOpenFor.fieldKey, mapped.value, mapped.libraryRef);
    }
    setPickerOpenFor(null);
  };

  // R2c: pending gate state for safety-sensitive picks (formation templates).
  const [pendingGate, setPendingGate] = useState(null);
  const commitGate = () => {
    if (!pendingGate) return;
    const { item, libraryKind, fieldKey } = pendingGate;
    const mapped = fieldValuesFromLibraryItem(libraryKind, item, fieldKey);
    if (mapped) setFieldFromLibrary(fieldKey, mapped.value, mapped.libraryRef);
    setPendingGate(null);
  };
  const formationGateSummary = pendingGate && pendingGate.libraryKind === "formation_template_section"
    ? (() => {
        const it = pendingGate.item;
        const rows = [
          ["Template", it.templateName],
          ["Section", it.sectionName],
          ["TVD (ft)", String(it.depth_ft)],
          ["Hole size (in)", String(it.hole_size_in)],
          ["Casing size (in)", String(it.casing_size_in)],
          ["Planning MW (ppg)", String(it.mud_weight_ppg)],
          ["Planning FG (psi/ft)", String(it.fracture_gradient_psi_ft)],
        ];
        return (
          <table data-testid="formation-gate-summary-table" className="w-full text-[11.5px] leading-tight border-collapse">
            <tbody>
              {rows.map(([k, v]) => (
                <tr key={k} className="border-b last:border-0" style={{ borderColor: "rgba(148,163,184,0.18)" }}>
                  <td className="py-1 pr-3 text-[var(--rs-text-3)] whitespace-nowrap">{k}</td>
                  <td className="py-1 text-[var(--rs-text)] font-mono-rs">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      })()
    : null;

  const handleCalculate = () => {
    // RC-1 Freemium: free calculators are always runnable; premium
    // calculators trigger the Subscribe modal for non-Pro users.
    if (!isPro && !isCalculatorAccessible(ent, calc.id)) {
      // P0: show a clearly visible centered modal instead of a small toast.
      // Calculation remains blocked. Modal CTA routes to /subscribe.
      trackPremiumBlocked(calc.id);
      trackUpgradeModalViewed(calc.id, "calculator_gate");
      setUpgradeModalOpen(true);
      return;
    }
    setError(null);
    setResult(null);
    // Validation pass on raw input
    for (const f of calc.fields) {
      if (f.optional) continue;
      if (f.type === "select" || f.type === "text" || f.type === "sectionTable") continue;
      if (values[f.key] === "" || values[f.key] === null || values[f.key] === undefined) {
        setError(`Required field missing: ${f.label}`);
        return;
      }
      const n = Number(values[f.key]);
      if (Number.isNaN(n)) {
        setError(`Invalid number for ${f.label}`);
        return;
      }
    }
    // Convert metric inputs → imperial before calling formula
    const converted = {};
    for (const f of calc.fields) {
      const v = values[f.key];
      if (f.type === "select" || f.type === "text" || f.type === "sectionTable" || f.keepUnit) {
        converted[f.key] = v;
      } else if (v === "" || v === null || v === undefined) {
        converted[f.key] = v;
      } else {
        converted[f.key] = toImperialValue(v, f.unit, system);
      }
    }
    try {
      const out = calc.fn(converted);
      // R1a — append provenance lines for any field pre-filled from the
      // Reference Library. The math is unchanged; only the displayed steps
      // gain an audit-trail line per library-sourced value.
      const provenanceLines = [];
      for (const f of calc.fields) {
        const ref = libraryRefs[f.key];
        if (!ref) continue;
        const line = provenanceLineLongFromRef(ref);
        if (line) {
          provenanceLines.push(
            ref.overridden
              ? `User-entered value for ${f.label}: ${values[f.key]} (overrode reference item ${ref.id}; library value ${ref.originalValue}).`
              : line
          );
        }
      }
      const stepsWithProvenance = provenanceLines.length > 0
        ? [...(out.steps || []), "Reference Library provenance:", ...provenanceLines.map((l) => `  • ${l}`)]
        : out.steps;
      // Convert result number and inputs/extras back to metric for display
      const displayResult = {
        ...out,
        steps: stepsWithProvenance,
        displayUnit: getDisplayUnit(out.unit, system),
        displayResult: fromImperialValue(out.result, out.unit, system),
        system,
      };
      setResult(displayResult);
      pushRecent(calc.id);
      trackCalculatorAttempted(calc.id, "success");
    } catch (e) {
      setError(e.message || "Calculation error");
      trackCalculatorAttempted(calc.id, "error");
    }
  };

  const handleReset = () => {
    setValues(init);
    setResult(null);
    setError(null);
  };

  const handleFav = () => {
    try {
      const next = toggleFavorite(calc.id);
      setFav(next);
      toast.success(next ? "Added to favorites" : "Removed from favorites");
    } catch (e) {
      if (e instanceof FeatureLockedError) { _toastUpgrade(navigate, e.message); return; }
      throw e;
    }
  };

  const buildSavePayload = () => ({
    calcId: calc.id,
    calcName: calc.name,
    category: calc.category,
    inputs: result.inputs,
    formula: result.formula,
    steps: result.steps,
    result: result.result,
    unit: result.unit,
    displayResult: result.displayResult,
    displayUnit: result.displayUnit,
    extras: result.extras || null,
    wellName: wellName.trim() || null,
    rigName: rigName.trim() || null,
    note: note.trim() || null,
    system: result.system || "imperial",
    worksheetWarning: !!result.worksheetWarning,
    wellControl: !!calc.wellControl,
    stackMode: calc.category === "wellcontrol" ? (calc.stackMode || "surface") : null,
  });

  const handleSave = () => {
    if (!result) { toast.error("Run the calculation first"); return; }
    try {
      saveCalculation(buildSavePayload());
      toast.success("Calculation saved");
    } catch (e) {
      if (e instanceof FeatureLockedError) { _toastUpgrade(navigate, e.message); return; }
      throw e;
    }
  };

  const handlePDF = () => {
    if (!result) { toast.error("Run the calculation first"); return; }
    try {
      exportCalculationToPDF({ ...buildSavePayload(), createdAt: new Date().toISOString() });
      toast.success("PDF downloaded");
    } catch (e) {
      if (e instanceof FeatureLockedError) { _toastUpgrade(navigate, e.message); return; }
      toast.error("PDF export failed");
    }
  };

  const handleSavePDF = () => {
    if (!result) { toast.error("Run the calculation first"); return; }
    const payload = buildSavePayload();
    try {
      saveCalculation(payload);
    } catch (e) {
      if (e instanceof FeatureLockedError) { _toastUpgrade(navigate, e.message); return; }
      throw e;
    }
    try {
      exportCalculationToPDF({ ...payload, createdAt: new Date().toISOString() });
      toast.success("Saved and PDF downloaded");
    } catch (e) {
      if (e instanceof FeatureLockedError) { _toastUpgrade(navigate, e.message); return; }
      toast.success("Saved (PDF export failed)");
    }
  };

  const buildShareText = () => {
    const inputs = convertLabeledMap(result.inputs, system);
    const extras = result.extras ? convertLabeledMap(result.extras, system) : null;
    const lines = [
      `RigSentinel — ${calc.name}`,
      `Formula: ${result.formula}`,
      "",
      "Inputs:",
      ...Object.entries(inputs).map(([k, v]) => `  ${k}: ${v}`),
      "",
      "Steps:",
      ...result.steps.map((s) => `  • ${s}`),
      "",
      `Result: ${result.displayResult} ${result.displayUnit}`,
    ];
    if (system === "metric") lines.splice(1, 0, "Display units: METRIC (computed in imperial, results converted)");
    if (extras && Object.keys(extras).length > 0) {
      lines.push("", "Additional:");
      Object.entries(extras).forEach(([k, v]) => lines.push(`  ${k}: ${v}`));
    }
    lines.push("", "— RigSentinel · Verify all results before operational use.");
    return lines.join("\n");
  };

  const handleEmail = () => {
    if (!result) { toast.error("Run the calculation first"); return; }
    const subject = `RigSentinel — ${calc.name}${wellName ? ` · ${wellName}` : ""}`;
    const body = buildShareText();
    // mailto cannot attach a PDF; we open the email with a fully populated text body
    // and also trigger a PDF download so the user can attach it manually if desired.
    try {
      exportCalculationToPDF({ ...buildSavePayload(), createdAt: new Date().toISOString() });
    } catch { /* non-fatal */ }
    const href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
    toast.success("Email draft opened · PDF downloaded for attachment");
  };

  const handleShare = async () => {
    if (!result) { toast.error("Run the calculation first"); return; }
    const text = buildShareText();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard");
      } else {
        toast.message("Share text", { description: text });
      }
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div className="flex flex-col gap-5" data-testid={`calc-${calc.id}`}>
      <SubscribeModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        featureId={calc.id}
        source="calculator_gate"
      />
      <LibraryPicker
        open={!!pickerOpenFor}
        onClose={() => setPickerOpenFor(null)}
        libraryKinds={pickerOpenFor ? (Array.isArray(pickerOpenFor.libraryKind) ? pickerOpenFor.libraryKind : [pickerOpenFor.libraryKind]) : []}
        anchorLabel={pickerOpenFor?.anchorLabel || ""}
        onPick={handleLibraryPick}
      />
      <SafetyConfirmationGate
        open={!!pendingGate}
        kind={pendingGate?.libraryKind === "formation_template_section" ? "formation" : "bop"}
        summary={formationGateSummary}
        onCancel={() => setPendingGate(null)}
        onConfirm={commitGate}
      />
      <div>
        <div className="flex items-start gap-2 flex-wrap">
          <h1 className="font-heading text-2xl font-black tracking-tight" data-testid="calc-title">{calc.name}</h1>
          {calc.category === "wellcontrol" && (
            <span
              data-testid={`stack-mode-badge-${calc.stackMode || "surface"}`}
              className="label-up text-[10px] px-2 py-0.5 rounded mt-1"
              style={
                (calc.stackMode || "surface") === "subsea"
                  ? { background: "rgba(33,150,243,0.15)", color: "#60A5FA", border: "1px solid rgba(33,150,243,0.4)" }
                  : { background: "rgba(255,179,0,0.15)", color: "var(--rs-amber)", border: "1px solid rgba(255,179,0,0.4)" }
              }
            >
              {(calc.stackMode || "surface") === "subsea" ? "Subsea" : "Surface"}
            </span>
          )}
          {calc.comingSoon && (
            <span data-testid="coming-soon-badge" className="label-up text-[10px] px-2 py-0.5 rounded mt-1" style={{ background: "rgba(255,179,0,0.15)", color: "var(--rs-amber)", border: "1px solid rgba(255,179,0,0.4)" }}>
              Coming Soon
            </span>
          )}
        </div>
        <p className="text-sm text-[var(--rs-text-2)] mt-1.5 leading-relaxed">{calc.description}</p>
      </div>

      {calc.stackMode === "subsea" && (
        <SafetyBanner subsea />
      )}

      {calc.comingSoon ? (
        <section className="card-rs p-5 text-center" data-testid="coming-soon-card" style={{ borderColor: "var(--rs-amber)" }}>
          <div className="font-heading font-black text-lg text-[var(--rs-amber)] mb-1.5">Advanced — Coming Soon</div>
          <p className="text-sm text-[var(--rs-text-2)] leading-relaxed">
            This calculator depends on company-specific models, fluid rheology, geometry, or survey context.
            It is being prepared for a future release. In the meantime, use your company-approved workflow
            or the related calculators in this category.
          </p>
          <button
            onClick={handleFav}
            data-testid="fav-btn"
            className="btn-ghost h-10 px-4 rounded-md inline-flex items-center gap-2 text-xs label-up mt-4"
          >
            <Star className={`w-4 h-4 ${fav ? "fill-current" : ""}`} style={{ color: fav ? "var(--rs-amber)" : undefined }} />
            {fav ? "Favorited" : "Favorite for later"}
          </button>
        </section>
      ) : (
      <>
      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={handleFav}
          data-testid="fav-btn"
          className="btn-ghost h-10 px-3 rounded-md inline-flex items-center gap-2 text-xs label-up"
          aria-pressed={fav}
        >
          <Star className={`w-4 h-4 ${fav ? "fill-current" : ""}`} style={{ color: fav ? "var(--rs-amber)" : undefined }} />
          {fav ? "Favorited" : "Favorite"}
        </button>
        <button onClick={handleReset} data-testid="reset-btn" className="btn-ghost h-10 px-3 rounded-md inline-flex items-center gap-2 text-xs label-up">
          <RotateCcw className="w-4 h-4" /> Reset
        </button>

        {/* Units toggle */}
        <div className="ml-auto inline-flex rounded-md border" style={{ borderColor: "var(--rs-border)" }} data-testid="units-toggle">
          <button
            onClick={() => setSystem("imperial")}
            data-active={system === "imperial"}
            data-testid="units-toggle-imperial"
            className="unit-pill rounded-none rounded-l-md border-0"
            style={{ borderRight: "1px solid var(--rs-border)" }}
          >IMP</button>
          <button
            onClick={() => setSystem("metric")}
            data-active={system === "metric"}
            data-testid="units-toggle-metric"
            className="unit-pill rounded-none rounded-r-md border-0"
          >MET</button>
        </div>
      </div>

      {calc.prefillAction === "subseaProfile" && (
        <div className="flex flex-col gap-2" data-testid="subsea-profile-actions">
          <PrefillFromSubseaProfileButton values={values} setValues={setValues} />
          <SaveWorksheetAsSubseaProfileButton values={values} />
        </div>
      )}

      {/* Inputs */}
      <section className="card-rs p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="label-up text-[var(--rs-text-3)]">Inputs</div>
          <div className="label-up text-[10px] text-[var(--rs-text-3)]">{system === "metric" ? "Metric mode" : "Imperial"}</div>
        </div>
        <div className="grid grid-cols-1 gap-3">
          {calc.fields.filter((f) => isFieldVisible(f, values)).map((f) => {
            const displayUnit = f.keepUnit ? f.unit : getDisplayUnit(f.unit, system);
            const opposite = (f.type === "select" || f.type === "text" || f.type === "sectionTable" || f.keepUnit) ? null : getOppositeReading(values[f.key], f.unit, system);
            return (
              <div key={f.key}>
                <label className="block text-xs font-semibold text-[var(--rs-text-2)] mb-1.5">
                  {f.label} {displayUnit && <span className="text-[var(--rs-text-3)] font-mono-rs ml-1">({displayUnit})</span>}
                  {f.optional && <span className="text-[var(--rs-text-3)] ml-1.5">— optional</span>}
                </label>
                {f.type === "select" ? (
                  <select
                    data-testid={`input-${f.key}`}
                    className="input-rs"
                    value={values[f.key]}
                    onChange={(e) => setField(f.key, e.target.value)}
                  >
                    {f.options.map((o) => (
                      <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>
                    ))}
                  </select>
                ) : f.type === "sectionTable" ? (
                  <SectionTable
                    value={values[f.key] || ""}
                    onChange={(csv) => setField(f.key, csv)}
                    spec={f.sectionTableSpec || null}
                  />
                ) : f.type === "text" ? (
                  f.multiline ? (
                    <textarea
                      data-testid={`input-${f.key}`}
                      className="input-rs"
                      style={{ height: "auto", minHeight: "5rem", padding: "0.625rem 0.875rem", fontFamily: "JetBrains Mono, monospace" }}
                      rows={f.rows || 4}
                      placeholder={f.placeholder || ""}
                      value={values[f.key]}
                      onChange={(e) => setField(f.key, e.target.value)}
                    />
                  ) : (
                    <input
                      data-testid={`input-${f.key}`}
                      type="text"
                      className="input-rs"
                      placeholder={f.placeholder || ""}
                      value={values[f.key]}
                      onChange={(e) => setField(f.key, e.target.value)}
                    />
                  )
                ) : (
                  <input
                    data-testid={`input-${f.key}`}
                    type="number"
                    inputMode="decimal"
                    className="input-rs"
                    placeholder={f.placeholder || ""}
                    value={values[f.key]}
                    onChange={(e) => (f.libraryKind ? setFieldUserEdited(f.key, e.target.value) : setField(f.key, e.target.value))}
                  />
                )}
                {f.libraryKind && (
                  <div
                    data-testid={`library-field-row-${f.key}`}
                    className="mt-1.5 flex items-center gap-2 flex-wrap"
                  >
                    <button
                      type="button"
                      data-testid={`library-pick-${f.key}`}
                      onClick={() => openLibraryPicker(f)}
                      className="h-7 px-2.5 rounded-md text-[10px] uppercase tracking-[0.08em] font-semibold border border-[var(--rs-border)] text-[var(--rs-text-dim,#9aa6b2)] hover:text-[var(--rs-amber,#f5b400)] hover:border-[var(--rs-amber,#f5b400)] transition-colors inline-flex items-center gap-1.5"
                    >
                      Pick from library
                    </button>
                    {libraryRefs[f.key] && !libraryRefs[f.key].overridden && (
                      <div
                        data-testid={`library-badge-${f.key}`}
                        className="text-[10px] tracking-[0.04em] font-mono-rs flex flex-col sm:flex-row sm:items-center sm:gap-1.5 leading-tight"
                      >
                        <span
                          className="font-semibold text-amber-400"
                          title={`${libraryRefs[f.key].label} · source: ${libraryRefs[f.key].source} · v${libraryRefs[f.key].version}`}
                          data-testid={`library-pill-id-${f.key}`}
                        >
                          Loaded from {libraryRefs[f.key].source} #{libraryRefs[f.key].id}
                        </span>
                        <span
                          className="text-[var(--rs-text-3)] sm:before:content-['·'] sm:before:mr-1 sm:before:text-[var(--rs-text-3)]"
                          data-testid={`library-pill-tally-${f.key}`}
                        >
                          Verify against your tally
                        </span>
                      </div>
                    )}
                    {libraryRefs[f.key] && libraryRefs[f.key].overridden && (
                      <span
                        data-testid={`library-badge-${f.key}`}
                        className="text-[10px] uppercase tracking-[0.08em] font-semibold px-1.5 py-0.5 rounded border border-[var(--rs-border)] text-[var(--rs-text-dim,#9aa6b2)]"
                        title={`Was: ${libraryRefs[f.key].originalValue} (from ${libraryRefs[f.key].id})`}
                      >
                        Overridden
                      </span>
                    )}
                  </div>
                )}
                {opposite && (
                  <div
                    data-testid={`opposite-${f.key}`}
                    className="font-mono-rs text-[10px] text-[var(--rs-text-3)] mt-1 pl-1 tracking-wide"
                  >
                    {opposite}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <button
          onClick={handleCalculate}
          data-testid="calculate-btn"
          className="btn-amber w-full h-12 rounded-md mt-4 inline-flex items-center justify-center gap-2 label-up"
        >
          <Play className="w-4 h-4" /> Calculate
        </button>
        {error && (
          <div
            data-testid="calc-error"
            className="mt-3 p-2.5 rounded-md border flex items-start gap-2"
            style={{ background: "rgba(244,67,54,0.1)", borderColor: "#C62828", color: "#FCA5A5" }}
          >
            <AlertCircle className="w-4 h-4 mt-0.5" />
            <span className="text-xs">{error}</span>
          </div>
        )}
      </section>

      {/* Result */}
      {result && (
        <section className="result-sheet p-4" data-testid="result-sheet">
          <div className="flex items-center justify-between mb-3">
            <div className="label-up text-[var(--rs-amber)] flex items-center gap-2">
              <Check className="w-4 h-4" /> Calculation Sheet
            </div>
            <div className="font-mono-rs text-[10px] text-[var(--rs-text-3)]">{new Date().toLocaleString()}</div>
          </div>

          <div className="ticker p-3 rounded-md mb-3">
            <div className="label-up text-[var(--rs-text-3)] mb-1.5">Final Answer</div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <div data-testid="result-value" className="font-mono-rs text-4xl font-bold tracking-tighter text-[var(--rs-text)]">
                {String(result.displayResult)}
              </div>
              <div data-testid="result-unit" className="font-mono-rs text-base text-[var(--rs-amber)]">{result.displayUnit}</div>
            </div>
            {system === "metric" && (
              <div className="text-[10px] text-[var(--rs-text-3)] mt-1.5">
                Imperial: <span className="font-mono-rs text-[var(--rs-text-2)]">{String(result.result)} {result.unit}</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3">
            <SheetRow label="Formula">
              <span className="font-mono-rs text-sm text-[var(--rs-text)]">{result.formula}</span>
              {system === "metric" && (
                <div className="text-[10px] text-[var(--rs-text-3)] mt-1">
                  Computed in imperial base units (ppg, psi, bbl, ft) — display converted to metric.
                </div>
              )}
            </SheetRow>
            <SheetRow label="Inputs">
              <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                {Object.entries(convertLabeledMap(result.inputs, system)).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 text-xs">
                    <span className="text-[var(--rs-text-3)]">{k}</span>
                    <span className="font-mono-rs text-[var(--rs-text)]">{String(v)}</span>
                  </div>
                ))}
              </div>
            </SheetRow>
            <SheetRow label="Step-by-Step">
              <ol className="space-y-1 font-mono-rs text-xs text-[var(--rs-text-2)]">
                {result.steps.map((s, i) => (
                  <li key={i}>
                    <span className="text-[var(--rs-amber)] mr-2">{String(i + 1).padStart(2, "0")}</span>
                    {s}
                  </li>
                ))}
              </ol>
            </SheetRow>
            {result.extras && (
              <SheetRow label="Additional">
                {Object.entries(convertLabeledMap(result.extras, system)).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 text-xs">
                    <span className="text-[var(--rs-text-3)]">{k}</span>
                    <span className="font-mono-rs text-[var(--rs-text)]">{String(v)}</span>
                  </div>
                ))}
              </SheetRow>
            )}
          </div>

          {/* Optional save metadata */}
          <div className="mt-4 grid grid-cols-1 gap-2">
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="well-input" className="input-rs h-10" placeholder="Well name (optional)" value={wellName} onChange={(e) => setWellName(e.target.value)} />
              <input data-testid="rig-input" className="input-rs h-10" placeholder="Rig name (optional)" value={rigName} onChange={(e) => setRigName(e.target.value)} />
            </div>
            <input data-testid="note-input" className="input-rs h-10" placeholder="Notes (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>

          <div className="grid grid-cols-1 gap-2 mt-3">
            <button onClick={handleSavePDF} data-testid="save-pdf-btn" className="btn-amber h-12 rounded-md inline-flex items-center justify-center gap-2 label-up text-xs">
              <Zap className="w-4 h-4" /> Save &amp; PDF
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={handleSave} data-testid="save-btn" className="btn-ghost h-10 rounded-md inline-flex items-center justify-center gap-1.5 label-up text-xs">
                <Save className="w-4 h-4" /> Save
              </button>
              <button onClick={handlePDF} data-testid="pdf-btn" className="btn-ghost h-10 rounded-md inline-flex items-center justify-center gap-1.5 label-up text-xs">
                <FileDown className="w-4 h-4" /> PDF
              </button>
              <button onClick={handleShare} data-testid="share-btn" className="btn-ghost h-10 rounded-md inline-flex items-center justify-center gap-1.5 label-up text-xs">
                <Share2 className="w-4 h-4" /> Copy
              </button>
              <button onClick={handleEmail} data-testid="email-btn" className="btn-ghost h-10 rounded-md inline-flex items-center justify-center gap-1.5 label-up text-xs">
                <Mail className="w-4 h-4" /> Email
              </button>
            </div>
            <button onClick={handleReset} data-testid="reset-btn-2" className="btn-ghost h-10 rounded-md inline-flex items-center justify-center gap-1.5 label-up text-xs">
              <RotateCcw className="w-4 h-4" /> Reset
            </button>
          </div>
        </section>
      )}
      </>
      )}

      {/* Send-to result chips — push the result into a related calculator */}
      {result && relatedTo.length > 0 && (
        <div className="card-rs p-3" data-testid="send-to-card">
          <div className="label-up text-[var(--rs-amber)] mb-2 flex items-center gap-2">
            <ArrowRight className="w-3.5 h-3.5" /> Send Result To
          </div>
          <div className="flex flex-wrap gap-2">
            {relatedTo.map((r) => (
              <button
                key={r.id}
                data-testid={`send-to-${r.id}`}
                onClick={() => handleSendTo(r)}
                className="btn-ghost h-9 px-3 rounded-md inline-flex items-center gap-2 text-xs"
              >
                <span className="text-[var(--rs-text-2)]">{r.target.name}</span>
                <span className="font-mono-rs text-[10px] text-[var(--rs-text-3)]">·</span>
                <span className="font-mono-rs text-[10px] text-[var(--rs-text-3)]">{r.fieldKey}</span>
                {r.note && <span className="text-[10px] text-[var(--rs-text-3)]">({r.note})</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Dependency graph block — Used in / Inputs from */}
      {!calc.comingSoon && (relatedTo.length > 0 || relatedFrom.length > 0) && (
        <div className="card-rs p-3" data-testid="dep-graph">
          <div className="label-up text-[var(--rs-text-3)] mb-2 flex items-center gap-2">
            <GitBranch className="w-3.5 h-3.5" /> Calculator Links
          </div>
          {relatedFrom.length > 0 && (
            <div className="mb-2">
              <div className="label-up text-[10px] text-[var(--rs-text-3)] mb-1">Inputs from</div>
              <div className="flex flex-wrap gap-1.5">
                {relatedFrom.map((r) => (
                  <button
                    key={r.id}
                    data-testid={`dep-from-${r.id}`}
                    onClick={() => navigate(`/calc/${r.id}`)}
                    className="text-[11px] px-2 py-1 rounded border border-[var(--rs-border)] text-[var(--rs-text-2)] hover:border-[var(--rs-amber)]"
                  >
                    {r.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          {relatedTo.length > 0 && (
            <div>
              <div className="label-up text-[10px] text-[var(--rs-text-3)] mb-1">Used in</div>
              <div className="flex flex-wrap gap-1.5">
                {relatedTo.map((r) => (
                  <button
                    key={r.id}
                    data-testid={`dep-to-${r.id}`}
                    onClick={() => navigate(`/calc/${r.id}`)}
                    className="text-[11px] px-2 py-1 rounded border border-[var(--rs-border)] text-[var(--rs-text-2)] hover:border-[var(--rs-amber)]"
                  >
                    {r.target.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Worksheet draft banner (for MAASP / Pump Pressure Schedule / Well Kill) */}
      {result?.worksheetWarning && (
        <div
          data-testid="worksheet-warning"
          className="card-rs p-3 text-xs"
          style={{ borderColor: "#C62828", color: "#FCA5A5", background: "rgba(244,67,54,0.08)" }}
        >
          <strong className="text-[var(--rs-danger)] mr-1">DRAFT</strong>
          This output is a computational worksheet for qualified review only. It is not a live well-control
          instruction. Final decisions require certified personnel, company procedures, and regulatory compliance.
        </div>
      )}

      <SafetyBanner />
      {calc.wellControl && <SafetyBanner wellControl />}

      {/* Persistent compact footer disclaimer + Safety Center link */}
      <div
        data-testid="calc-footer-disclaimer"
        className="card-rs p-3 mt-1 flex items-start gap-2.5"
        style={{
          background: "rgba(22,24,29,0.6)",
          borderColor: "var(--rs-border)",
        }}
      >
        <AlertCircle
          className="w-3.5 h-3.5 mt-0.5 shrink-0"
          style={{ color: "var(--rs-text-3)" }}
        />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] leading-snug text-[var(--rs-text-3)]">
            <span
              className="font-semibold"
              style={{ color: "var(--rs-text-2)" }}
            >
              Calculation-support tool.
            </span>{" "}
            Verify all inputs, units, formulas, assumptions, and results before
            use. This tool does not replace competent personnel, approved
            procedures, engineering judgment, manufacturer data, or regulatory
            requirements.
          </p>
          <button
            type="button"
            data-testid="calc-safety-center-link"
            onClick={() => navigate("/safety")}
            className="text-[11px] mt-1.5 inline-flex items-center gap-1 font-semibold"
            style={{ color: "var(--rs-amber)" }}
          >
            View Safety Center <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function SheetRow({ label, children }) {
  return (
    <div className="border-t border-[var(--rs-border)] pt-3">
      <div className="label-up text-[var(--rs-text-3)] mb-1.5">{label}</div>
      {children}
    </div>
  );
}


// =========================================================================
// Pre-fill from Subsea Profile — accessory action on the Subsea Kill Sheet.
// Maps profile keys → worksheet keys, validates each numeric value, warns
// before overwriting non-empty worksheet fields, and reports skipped /
// missing values to the user via toast notifications.
// =========================================================================
const _SUBSEA_PREFILL_MAP = [
  // [worksheet field key, profile key, kind ('num' or 'str')]
  ["waterDepth",        "waterDepth",            "num"],
  ["bopStackDepth",     "bopStackDepth",         "num"],
  ["rkbToMudline",      "rkbToMudline",          "num"],
  ["currentMW",         "currentMW",             "num"],
  ["scrPressure",       "scrPressure",           "num"],
  ["killRate",          "killRate",              "num"],
  ["pumpOutput",        "pumpOutput",            "num"],
  ["chokeFriction",     "chokeLineFriction",     "num"],
  ["killFriction",      "killLineFriction",      "num"],
  ["chokeFrictionRate", "chokeFrictionTestRate", "num"],
  ["killFrictionRate",  "killFrictionTestRate",  "num"],
  ["casingShoeTvd",     "casingShoeTvd",         "num"],
  ["chokeLineVol",      "chokeLineCapacity",     "num"],
  ["killLineVol",       "killLineCapacity",      "num"],
  ["riserVol",          "riserCapacity",         "num"],
  ["wellName",          "wellName",              "str"],
  ["rigName",           "rigName",               "str"],
  ["procedureRef",      "procedureRef",          "str"],
  ["userNotes",         "userNotes",             "str"],
];

function PrefillFromSubseaProfileButton({ values, setValues }) {
  const handleClick = () => {
    const profile = getSubseaProfile();
    if (!profile) {
      toast.error("No Subsea Well Control Profile found. Create or save a profile first.");
      return;
    }
    // 1. Build the candidate map and validate each entry.
    const candidates = []; // { ws, raw, valid, value, kind, profKey }
    for (const [ws, profKey, kind] of _SUBSEA_PREFILL_MAP) {
      const raw = profile[profKey];
      if (raw === undefined || raw === null || raw === "") continue;
      if (kind === "num") {
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0) {
          candidates.push({ ws, raw, valid: false, value: null, kind, profKey });
          continue;
        }
        candidates.push({ ws, raw, valid: true, value: String(raw), kind, profKey });
      } else {
        const s = String(raw).trim();
        if (s.length === 0) continue;
        candidates.push({ ws, raw, valid: true, value: s, kind, profKey });
      }
    }
    if (candidates.length === 0) {
      toast.error("Subsea profile has no usable values for this worksheet yet.");
      return;
    }
    // 2. Find collisions: worksheet fields that already have a value.
    const collisions = candidates.filter(
      (c) => c.valid && values[c.ws] !== "" && values[c.ws] !== undefined && values[c.ws] !== null && String(values[c.ws]) !== String(c.value),
    );
    if (collisions.length > 0) {
      const summary = collisions.map((c) => `• ${c.ws}: "${values[c.ws]}" → "${c.value}"`).join("\n");
      const ok = window.confirm(
        `Some worksheet fields already have values that would be replaced:\n\n${summary}\n\nReplace existing values with profile data?`,
      );
      if (!ok) {
        toast.info("Pre-fill cancelled. Existing worksheet values preserved.");
        return;
      }
    }
    // 3. Apply the valid candidates.
    setValues((prev) => {
      const next = { ...prev };
      candidates.filter((c) => c.valid).forEach((c) => {
        next[c.ws] = c.value;
      });
      return next;
    });
    const invalidList = candidates.filter((c) => !c.valid).map((c) => c.profKey);
    const appliedCount = candidates.filter((c) => c.valid).length;
    const filledKeys = candidates.filter((c) => c.valid).map((c) => c.ws);
    // Detect missing-but-mappable fields (the user has the worksheet field but
    // the profile didn't carry a usable value).
    const missingCount = _SUBSEA_PREFILL_MAP.length - filledKeys.length - invalidList.length;
    if (invalidList.length > 0) {
      toast.warning(`Skipped invalid profile values: ${invalidList.join(", ")}`);
    }
    if (appliedCount > 0 && (missingCount > 0 || invalidList.length > 0)) {
      toast.success("Profile values copied. Some worksheet fields still require manual entry.");
    } else if (appliedCount > 0) {
      toast.success(`Pre-filled ${appliedCount} field${appliedCount === 1 ? "" : "s"} from Subsea Well Control Profile.`);
    }
  };
  return (
    <div className="card-rs p-3 flex items-center justify-between gap-3" data-testid="subsea-prefill-row" style={{ borderColor: "#60A5FA" }}>
      <div className="min-w-0 pr-2">
        <div className="text-sm font-semibold" style={{ color: "#60A5FA" }}>Pre-fill from Subsea Profile</div>
        <div className="text-[11px] text-[var(--rs-text-3)] mt-0.5 leading-snug">
          Copies water depth, BOP / riser / choke / kill data, current MW, SCR, kill rate, pump output, and notes from your saved profile. Existing values are protected — you'll be asked before any overwrite.
        </div>
      </div>
      <button
        type="button"
        data-testid="subsea-prefill-btn"
        onClick={handleClick}
        className="btn-ghost h-10 px-3 rounded-md inline-flex items-center gap-2 text-xs label-up shrink-0"
        style={{ borderColor: "#60A5FA", color: "#60A5FA" }}
      >
        <DownloadIcon className="w-4 h-4" /> Pre-fill
      </button>
    </div>
  );
}

// =========================================================================
// Save Current Worksheet as Subsea Profile — inverse of pre-fill.
// Maps worksheet keys → profile keys with the same validation logic.
// =========================================================================
const _SUBSEA_SAVEBACK_MAP = [
  // [worksheet key, profile key, kind]
  ["waterDepth",        "waterDepth",            "num"],
  ["bopStackDepth",     "bopStackDepth",         "num"],
  ["rkbToMudline",      "rkbToMudline",          "num"],
  ["currentMW",         "currentMW",             "num"],
  ["scrPressure",       "scrPressure",           "num"],
  ["killRate",          "killRate",              "num"],
  ["pumpOutput",        "pumpOutput",            "num"],
  ["chokeFriction",     "chokeLineFriction",     "num"],
  ["killFriction",      "killLineFriction",      "num"],
  ["chokeFrictionRate", "chokeFrictionTestRate", "num"],
  ["killFrictionRate",  "killFrictionTestRate",  "num"],
  ["casingShoeTvd",     "casingShoeTvd",         "num"],
  ["chokeLineVol",      "chokeLineCapacity",     "num"],
  ["killLineVol",       "killLineCapacity",      "num"],
  ["riserVol",          "riserCapacity",         "num"],
  ["wellName",          "wellName",              "str"],
  ["rigName",           "rigName",               "str"],
  ["procedureRef",      "procedureRef",          "str"],
  ["userNotes",         "userNotes",             "str"],
];

function SaveWorksheetAsSubseaProfileButton({ values }) {
  const handleClick = () => {
    // 1. Build the candidate map with validation.
    const candidates = []; // { ws, profKey, kind, valid, value }
    for (const [ws, profKey, kind] of _SUBSEA_SAVEBACK_MAP) {
      const raw = values[ws];
      if (raw === undefined || raw === null) continue;
      if (typeof raw === "string" && raw.trim() === "") continue;
      if (kind === "num") {
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0) {
          candidates.push({ ws, profKey, kind, valid: false, value: null, raw });
          continue;
        }
        candidates.push({ ws, profKey, kind, valid: true, value: String(raw), raw });
      } else {
        const s = String(raw).trim();
        if (s.length === 0) continue;
        candidates.push({ ws, profKey, kind, valid: true, value: s, raw });
      }
    }
    const valid = candidates.filter((c) => c.valid);
    const invalid = candidates.filter((c) => !c.valid);
    if (valid.length === 0) {
      toast.error("No valid worksheet values available to save into the Subsea Profile.");
      return;
    }
    // 2. Overwrite protection.
    const existing = getSubseaProfile();
    if (existing) {
      const collisions = valid.filter((c) => {
        const before = existing[c.profKey];
        return before !== undefined && before !== null && String(before).trim() !== "" && String(before) !== String(c.value);
      });
      if (collisions.length > 0) {
        const summary = collisions.map((c) => `• ${c.profKey}: "${existing[c.profKey]}" → "${c.value}"`).join("\n");
        const ok = window.confirm(
          `This will replace ${collisions.length} field${collisions.length === 1 ? "" : "s"} in the saved Subsea Well Control Profile:\n\n${summary}\n\nProceed?`,
        );
        if (!ok) {
          toast.info("Save cancelled. Existing profile values preserved.");
          return;
        }
      }
    }
    // 3. Merge and save.
    const next = { ...(existing || {}) };
    valid.forEach((c) => { next[c.profKey] = c.value; });
    saveSubseaProfile(next);
    // 4. Feedback.
    if (invalid.length > 0) {
      toast.warning(`Skipped invalid worksheet values: ${invalid.map((c) => c.ws).join(", ")}`);
    }
    const missing = _SUBSEA_SAVEBACK_MAP.length - valid.length - invalid.length;
    if (missing > 0 || invalid.length > 0) {
      toast.success("Subsea Profile updated from worksheet. Some profile fields may still require manual entry.");
    } else {
      toast.success(`Subsea Profile updated · ${valid.length} field${valid.length === 1 ? "" : "s"} saved.`);
    }
  };
  return (
    <div className="card-rs p-3 flex items-center justify-between gap-3" data-testid="subsea-saveback-row" style={{ borderColor: "#60A5FA" }}>
      <div className="min-w-0 pr-2">
        <div className="text-sm font-semibold" style={{ color: "#60A5FA" }}>Save Current Worksheet as Subsea Profile</div>
        <div className="text-[11px] text-[var(--rs-text-3)] mt-0.5 leading-snug">
          Copies the matching worksheet values back into your saved Subsea Well Control Profile so future sessions can pre-fill from them. Existing profile values are protected — you'll be asked before any overwrite.
        </div>
      </div>
      <button
        type="button"
        data-testid="subsea-saveback-btn"
        onClick={handleClick}
        className="btn-ghost h-10 px-3 rounded-md inline-flex items-center gap-2 text-xs label-up shrink-0"
        style={{ borderColor: "#60A5FA", color: "#60A5FA" }}
      >
        <UploadIcon className="w-4 h-4" /> Save Profile
      </button>
    </div>
  );
}

